/**
 * Service layer for Resource Board API operations
 */
import apiClient from './apiClient';
import logger from '../utils/logger';

// Simple in-memory cache with TTL + sessionStorage for persistence
const cache = new Map();
const CACHE_TTL = {
  users: 5 * 60 * 1000,      // 5 minutes - users rarely change
  projects: 5 * 60 * 1000,   // 5 minutes - projects rarely change
  globalRoles: 10 * 60 * 1000, // 10 minutes - roles very rarely change
  cars: 5 * 60 * 1000,       // 5 minutes - cars rarely change
};

const STORAGE_PREFIX = 'rb_cache_';

const getCached = (key) => {
  // First check memory cache
  const memCached = cache.get(key);
  if (memCached && Date.now() < memCached.expiry) {
    logger.debug(`Memory cache hit for: ${key}`);
    return memCached.data;
  }
  cache.delete(key);

  // Then check sessionStorage
  try {
    const stored = sessionStorage.getItem(STORAGE_PREFIX + key);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Date.now() < parsed.expiry) {
        logger.debug(`Session cache hit for: ${key}`);
        // Restore to memory cache
        cache.set(key, parsed);
        return parsed.data;
      }
      sessionStorage.removeItem(STORAGE_PREFIX + key);
    }
  } catch (e) {
    // sessionStorage not available or parsing error
  }

  return null;
};

const setCache = (key, data, ttl) => {
  const cacheEntry = { data, expiry: Date.now() + ttl };
  cache.set(key, cacheEntry);

  // Also save to sessionStorage for persistence across page reloads
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(cacheEntry));
  } catch (e) {
    // sessionStorage full or not available - ignore
  }
};

// Clear cache for specific key or all
const clearCache = (key = null) => {
  if (key) {
    cache.delete(key);
    try {
      sessionStorage.removeItem(STORAGE_PREFIX + key);
    } catch (e) {}
  } else {
    cache.clear();
    try {
      Object.keys(sessionStorage)
        .filter(k => k.startsWith(STORAGE_PREFIX))
        .forEach(k => sessionStorage.removeItem(k));
    } catch (e) {}
  }
};

class ResourceBoardService {
  /**
   * Fetch all resource planning assignments
   */
  async getAssignments(filters = {}) {
    logger.debug('Fetching assignments with filters:', filters);
    return apiClient.get('/api/resource-planning-assignments/', { params: filters });
  }

  /**
   * Create new assignment
   */
  async createAssignment(data) {
    logger.info('Creating assignment:', data);
    return apiClient.post('/api/resource-planning-assignments/', data);
  }

  /**
   * Update existing assignment
   */
  async updateAssignment(id, data) {
    logger.info('Updating assignment:', id, data);
    return apiClient.put(`/api/resource-planning-assignments/${id}/`, data);
  }

  /**
   * Patch assignment (partial update)
   */
  async patchAssignment(id, data) {
    logger.info('Patching assignment:', id, data);
    return apiClient.patch(`/api/resource-planning-assignments/${id}/`, data);
  }

  /**
   * Update assignment dates
   */
  async updateAssignmentDates(id, startDate, endDate) {
    logger.info('Updating assignment dates:', id, startDate, endDate);
    return apiClient.patch(`/api/resource-planning-assignments/${id}/update_dates/`, {
      start_date: startDate,
      end_date: endDate
    });
  }

  /**
   * Update vehicle assignment dates
   */
  async updateVehicleAssignmentDates(id, startDate, endDate) {
    logger.info('Updating vehicle assignment dates:', id, startDate, endDate);
    return apiClient.patch(`/api/vehicle-assignments/${id}/`, {
      assigned_date: startDate,
      expected_return_date: endDate
    });
  }

  /**
   * Delete assignment
   */
  async deleteAssignment(id) {
    logger.info('Deleting assignment:', id);
    return apiClient.delete(`/api/resource-planning-assignments/${id}/`);
  }

  /**
   * Fetch projects
   */
  async getProjects(filters = {}) {
    logger.debug('Fetching projects');
    return apiClient.get('/api/project/', { params: filters });
  }

  /**
   * Fetch users (with cache)
   */
  async getUsers(filters = {}) {
    const cacheKey = 'users';
    const cached = getCached(cacheKey);
    if (cached && Object.keys(filters).length === 0) {
      return cached;
    }
    logger.debug('Fetching users from API');
    const data = await apiClient.get('/api/users/', { params: filters });
    if (Object.keys(filters).length === 0) {
      setCache(cacheKey, data, CACHE_TTL.users);
    }
    return data;
  }

  /**
   * Fetch cars (with cache)
   */
  async getCars(filters = {}) {
    const cacheKey = 'cars';
    const cached = getCached(cacheKey);
    if (cached && Object.keys(filters).length === 0) {
      return cached;
    }
    logger.debug('Fetching cars from API');
    const data = await apiClient.get('/api/cars/', { params: filters });
    if (Object.keys(filters).length === 0) {
      setCache(cacheKey, data, CACHE_TTL.cars);
    }
    return data;
  }

  /**
   * Fetch car assignments
   */
  async getCarAssignments(date) {
    logger.debug('Fetching car assignments for date:', date);
    return apiClient.get('/api/car-assignments/', { params: { date } });
  }

  /**
   * Create car assignment
   */
  async createCarAssignment(data) {
    logger.info('Creating car assignment:', data);
    return apiClient.post('/api/car-assignments/', data);
  }

  /**
   * Update car assignment (full update)
   */
  async updateCarAssignment(id, data) {
    logger.info('Updating car assignment:', id, data);
    return apiClient.put(`/api/car-assignments/${id}/`, data);
  }

  /**
   * Patch car assignment (partial update)
   */
  async patchCarAssignment(id, data) {
    logger.info('Patching car assignment:', id, data);
    return apiClient.patch(`/api/car-assignments/${id}/`, data);
  }

  /**
   * Delete car assignment
   */
  async deleteCarAssignment(id) {
    logger.info('Deleting car assignment:', id);
    return apiClient.delete(`/api/car-assignments/${id}/`);
  }

  /**
   * Fetch travel data
   */
  async getTravelData(filters = {}) {
    logger.debug('Fetching travel data');
    return apiClient.get('/api/car-travel/', { params: filters });
  }

  /**
   * Create travel
   */
  async createTravel(data) {
    logger.info('Creating travel:', data);
    return apiClient.post('/api/car-travel/', data);
  }

  /**
   * Update travel
   */
  async updateTravel(id, data) {
    logger.info('Updating travel:', id, data);
    return apiClient.put(`/api/car-travel/${id}/`, data);
  }

  /**
   * Delete travel
   */
  async deleteTravel(id) {
    logger.info('Deleting travel:', id);
    return apiClient.delete(`/api/car-travel/${id}/`);
  }

  /**
   * Fetch project roles
   */
  async getProjectRoles(projectId) {
    logger.debug('Fetching project roles for:', projectId);
    return apiClient.get(`/api/project/${projectId}/roles/`);
  }

  /**
   * Create project role
   */
  async createProjectRole(projectId, data) {
    logger.info('Creating project role:', projectId, data);
    return apiClient.post(`/api/project/${projectId}/roles/`, data);
  }

  /**
   * Fetch global roles (with cache)
   */
  async getGlobalRoles() {
    const cacheKey = 'globalRoles';
    const cached = getCached(cacheKey);
    if (cached) {
      return cached;
    }
    logger.debug('Fetching global roles from API');
    const data = await apiClient.get('/api/global-roles/');
    setCache(cacheKey, data, CACHE_TTL.globalRoles);
    return data;
  }

  /**
   * Fetch all project roles (with cache)
   */
  async getAllProjectRoles() {
    const cacheKey = 'projectRoles';
    const cached = getCached(cacheKey);
    if (cached) {
      return cached;
    }
    logger.debug('Fetching all project roles from API');
    const data = await apiClient.get('/api/project-roles/');
    setCache(cacheKey, data, CACHE_TTL.globalRoles);
    return data;
  }

  /**
   * Fetch user global roles (with cache)
   */
  async getUserGlobalRoles() {
    const cacheKey = 'userGlobalRoles';
    const cached = getCached(cacheKey);
    if (cached) {
      return cached;
    }
    logger.debug('Fetching user global roles from API');
    const data = await apiClient.get('/api/user-global-roles/');
    setCache(cacheKey, data, CACHE_TTL.globalRoles);
    return data;
  }

  /**
   * Fetch travel data (alternative endpoint)
   */
  async getTravel(filters = {}) {
    logger.debug('Fetching travel');
    return apiClient.get('/api/travel/', { params: filters });
  }

  /**
   * Fetch trainings
   */
  async getTrainings(filters = {}) {
    logger.debug('Fetching trainings');
    return apiClient.get('/api/trainings/', { params: filters });
  }

  /**
   * Fetch vehicle assignments
   */
  async getVehicleAssignments(filters = {}) {
    logger.debug('Fetching vehicle assignments');
    return apiClient.get('/api/vehicle-assignments/', { params: filters });
  }

  /**
   * Create vehicle assignment
   */
  async createVehicleAssignment(data) {
    logger.info('Creating vehicle assignment:', data);
    return apiClient.post('/api/vehicle-assignments/', data);
  }

  /**
   * Delete vehicle assignment
   */
  async deleteVehicleAssignment(id) {
    logger.info('Deleting vehicle assignment:', id);
    return apiClient.delete(`/api/vehicle-assignments/${id}/`);
  }

  /**
   * Fetch projects (alternative endpoint, with cache)
   */
  async getProjectsAlt(filters = {}) {
    const cacheKey = 'projects';
    const cached = getCached(cacheKey);
    if (cached && Object.keys(filters).length === 0) {
      return cached;
    }
    logger.debug('Fetching projects from API');
    const data = await apiClient.get('/api/projects/', { params: filters });
    if (Object.keys(filters).length === 0) {
      setCache(cacheKey, data, CACHE_TTL.projects);
    }
    return data;
  }

  /**
   * Fetch resource notes
   */
  async getNotes(filters = {}) {
    logger.debug('Fetching notes');
    return apiClient.get('/api/resource-notes/', { params: filters });
  }

  /**
   * Create note
   */
  async createNote(data) {
    logger.info('Creating note:', data);
    return apiClient.post('/api/resource-notes/', data);
  }

  /**
   * Update note
   */
  async updateNote(id, data) {
    logger.info('Updating note:', id, data);
    return apiClient.put(`/api/resource-notes/${id}/`, data);
  }

  /**
   * Delete note
   */
  async deleteNote(id) {
    logger.info('Deleting note:', id);
    return apiClient.delete(`/api/resource-notes/${id}/`);
  }

  /**
   * Batch operations
   */
  async batchCreateAssignments(assignments) {
    logger.info('Batch creating assignments:', assignments.length);
    return apiClient.post('/api/resource-planning-assignments/batch/', { assignments });
  }

  async batchUpdateAssignments(updates) {
    logger.info('Batch updating assignments:', updates.length);
    return apiClient.put('/api/resource-planning-assignments/batch/', { updates });
  }

  async batchDeleteAssignments(ids) {
    logger.info('Batch deleting assignments:', ids.length);
    return apiClient.delete('/api/resource-planning-assignments/batch/', { data: { ids } });
  }

  /**
   * Update user (enable/disable, etc.)
   */
  async updateUser(userId, data) {
    logger.info('Updating user:', userId, data);
    return apiClient.patch(`/api/users/${userId}/`, data);
  }

  /**
   * Create or update user global role assignment
   */
  async assignUserGlobalRole(userId, roleId) {
    logger.info('Assigning global role to user:', userId, roleId);
    return apiClient.post('/api/user-global-roles/', {
      user: userId,
      role: roleId
    });
  }

  /**
   * Delete user global role assignment
   */
  async removeUserGlobalRole(assignmentId) {
    logger.info('Removing user global role:', assignmentId);
    return apiClient.delete(`/api/user-global-roles/${assignmentId}/`);
  }

  /**
   * Get role order setting
   */
  async getRoleOrderSetting() {
    logger.debug('Fetching role order setting');
    return apiClient.get('/api/settings/role_order/').catch(() => ({ role_order: [] }));
  }

  /**
   * Save role order setting
   */
  async saveRoleOrderSetting(roleOrder) {
    logger.info('Saving role order setting:', roleOrder);
    return apiClient.post('/api/settings/role_order/', { role_order: roleOrder });
  }

  /**
   * Fetch holidays
   */
  async getHolidays(filters = {}) {
    logger.debug('Fetching holidays with filters:', filters);
    return apiClient.get('/api/holidays/', { params: filters });
  }

  /**
   * Create holiday
   */
  async createHoliday(data) {
    logger.info('Creating holiday:', data);
    return apiClient.post('/api/holidays/', data);
  }

  /**
   * Update holiday
   */
  async updateHoliday(id, data) {
    logger.info('Updating holiday:', id, data);
    return apiClient.patch(`/api/holidays/${id}/`, data);
  }

  /**
   * Delete holiday
   */
  async deleteHoliday(id) {
    logger.info('Deleting holiday:', id);
    return apiClient.delete(`/api/holidays/${id}/`);
  }

  /**
   * Approve holiday
   */
  async approveHoliday(id) {
    logger.info('Approving holiday:', id);
    return apiClient.post(`/api/holidays/${id}/approve/`);
  }

  /**
   * Reject holiday
   */
  async rejectHoliday(id, reason) {
    logger.info('Rejecting holiday:', id, reason);
    return apiClient.post(`/api/holidays/${id}/reject/`, { reason });
  }
}

// Export singleton instance
const resourceBoardService = new ResourceBoardService();
export default resourceBoardService;
